






















































































































































import { CreateElement } from 'vue';
import { defineComponent, reactive, ref, watch, computed, onUnmounted, getCurrentInstance } from '@vue/composition-api';
import { Route } from 'vue-router';
import { HelpCircleIcon, LinkIcon, ErrorCircleFilledIcon, FileCopyIcon } from 'tdesign-icons-vue';
import { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import store from '@/renderer/store';
import tips from '@/renderer/scripts/utils/tips';
import { formatError } from '@/renderer/scripts/utils/errorCode';
import { copyText2Clipboard } from '@/renderer/scripts/utils/tools/index';
import { dispatchRouter } from '@/renderer/scripts/utils/tools/dispatchRouter';
import ConstantConfig, { inWeBrowser } from '@/renderer/scripts/utils/ConstantConfig';
import { httpUrl, CONSTANT_URL } from '@/renderer/scripts/http';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';
import IPList from './components/IPRestrictionList.vue';
import IPRestrictionConfig from './components/IPRestrictionConfig.vue';

// 静态变量
const steps = [
  {
    status: 'process',
    title: i18n_t('域名准备'),
    content: (h: CreateElement) =>
      h('p', [
        i18n_t('如无域名，前往 '),
        h(
          'a',
          {
            class: 'link',
            on: {
              click() {
                dispatchRouter('https://dnspod.cloud.tencent.com');
              },
            },
          },
          [i18n_t('注册域名')],
        ),
      ]),
  },
  {
    status: 'process',
    title: i18n_t('域名备案'),
    content: (h: CreateElement) =>
      h('p', [
        i18n_t('需要完成域名备案，流程参考 '),
        h(
          'a',
          {
            class: 'link',
            on: {
              click() {
                dispatchRouter(CONSTANT_URL.domainUrl);
              },
            },
          },
          [i18n_t('网站备案')],
        ),
      ]),
  },
  {
    status: 'process',
    title: i18n_t('绑定域名'),
    content: (h: CreateElement) =>
      h('p', [i18n_t('在腾讯云企业网盘绑定自定义域名，关联的服务需允许公网访问，否则无法对外服务。')]),
  },
  {
    status: 'process',
    title: i18n_t('添加CNAME记录'),
    content: (h: CreateElement) =>
      h('p', [i18n_t('前往DNS服务提供商（如 DNSPod）添加CNAME记录，将域名指向服务器CNAME域名。')]),
  },
];

// 备案号(icp备案号和公安备案号)正则判断,首尾不能是空
const fillingNumberPattern = /^(?!\s)[\u4E00-\u9FFF0-9a-zA-Z\-. ]{10,100}(?<!\s)$/;
const newDomainRules = [
  {
    required: true,
    message: i18n_t('域名为必填项'),
    type: 'error',
    trigger: 'change',
  },
  {
    validator: (value: string) => {
      const parts = value.split('.');
      const lastPart = parts[parts.length - 1];
      return (
        parts.length > 1 &&
        parts.every(part => /^[a-z0-9]([a-z0-9-]{0,61}(?=[a-z0-9])[a-z0-9]|[a-z0-9]{0,62})$/gi.test(part)) &&
        /^[a-z]{2,63}$/gi.test(lastPart) &&
        value.length < 256
      );
    },
    message: i18n_t('域名格式不正确'),
  },
];

const commonKeyRule = {
  validator(value: string) {
    if (!value) return true;
    return value.startsWith('-----BEGIN RSA PRIVATE KEY-----') && value.endsWith('-----END RSA PRIVATE KEY-----');
  },
  message: i18n_t('密钥内容不正确'),
};
const commonCertRule = {
  validator(value: string) {
    if (!value) return true;
    return value.startsWith('-----BEGIN CERTIFICATE-----') && value.endsWith('-----END CERTIFICATE-----');
  },
  message: i18n_t('证书内容不正确'),
};
const commonFillingNumberRule = {
  validator(value: string) {
    if (!value) return true;
    return fillingNumberPattern.test(value);
  },
  message: i18n_t('备案号格式不正确'),
};
const commonTitleRule = {
  validator(value: string) {
    if (!value) return true;
    return value.length <= orgNameMaxLength;
  },
  message: i18n_t('网页名称字数超出限制'),
};
const rules = {
  newDomain: newDomainRules,
  domainKey: [commonKeyRule],
  domainCert: [commonCertRule],
  domainICP: [commonFillingNumberRule],
  domainPsb: [commonFillingNumberRule],
  domainTitle: [commonTitleRule],
};
const newBindingRules = {
  newDomain: newDomainRules,
  domainKey: [
    {
      required: true,
      message: i18n_t('证书密钥为必填项'),
      type: 'error',
      trigger: 'change',
    },
    commonKeyRule,
  ],

  domainCert: [
    {
      required: true,
      message: i18n_t('证书内容为必填项'),
      type: 'error',
      trigger: 'change',
    },
    commonCertRule,
  ],

  domainICP: [commonFillingNumberRule],
  domainPsb: [commonFillingNumberRule],
  domainTitle: [commonTitleRule],
};
const validDomainStatusList = [
  {
    status: 0,
    text: i18n_t('未生效'),
    theme: 'warning',
  },
  {
    status: 1,
    text: i18n_t('生效中'),
    theme: 'success',
  },
  {
    status: 2,
    text: i18n_t('已失效'),
    theme: 'warning',
  },
];

const { orgNameMaxLength } = ConstantConfig;

export default defineComponent({
  components: {
    HelpCircleIcon,
    LinkIcon,
    ErrorCircleFilledIcon,
    FileCopyIcon,
    IPList,
    IPRestrictionConfig,
  },
  // 组件路由守卫：本页未保存则拦截路由跳转
  beforeRouteLeave(to: Route, from: Route, next: Function) {
    const notSaveTips = i18n_t('确认离开当前页面？未保存的数据将会丢失。');
    if (this.stage === 'bind') {
      const dialog = CustomDialog.confirm({
        header: i18n_t('提示'),
        body: notSaveTips,
        theme: 'warning',
        onConfirm: async () => {
          if (dialog.hide) {
            dialog.hide();
          }
          next();
        },
      });
    } else {
      next();
    }
  },
  setup() {
    // 响应式变量
    const entId = ref('');
    const isSaving = ref(false);
    const stage = ref('index');
    const errMsg = ref('');
    const loaded = ref(false);
    const domainInfo = reactive<SMH.CustomDomainData>({
      domain: '',
      status: -1,
    });
    const domainFormData = reactive({
      domainKey: '',
      domainCert: '',
      newDomain: '',
      domainICP: '',
      domainPsb: '',
      domainTitle: '',
    });
    const internalWebUrl = ref(httpUrl.webUrl); // 私有化服务器 url
    // 计算变量
    const orgId = computed(() => store.getters.orgId);
    const currentDomainStatus = computed(() => {
      const [dms] = validDomainStatusList.filter(item => domainInfo.status === item.status);
      return dms;
    });
    const formReady = computed(() => {
      if (domainInfo.domain) {
        // 编辑，需要确认是否有改动，有改动才保存
        return (
          domainFormData.newDomain &&
          (domainInfo.domain !== domainFormData.newDomain ||
            domainFormData.domainKey ||
            domainFormData.domainCert ||
            domainInfo.icp !== domainFormData.domainICP ||
            domainInfo.psb !== domainFormData.domainPsb ||
            domainInfo.title !== domainFormData.domainTitle)
        );
      }
      // 新建，确认所有必填字段齐全
      return domainFormData.domainKey && domainFormData.domainCert && domainFormData.newDomain;
    });
    const methods = {
      async copyText(text: string) {
        try {
          await copyText2Clipboard(text);
          tips.success(
            i18n_t('内容已复制到剪贴板：{text}', {
              text,
            }),
          );
        } catch (e: any) {
          tips.error(e);
        }
      },
      dispatchRouter,
      async save({ firstError }: any) {
        isSaving.value = true;
        errMsg.value = '';
        if (firstError) {
          tips.error(firstError);
          isSaving.value = false;
        } else {
          const reqBody: SMH.CustomDomainParams = {
            domain: domainFormData.newDomain || '',
          };
          domainFormData.domainKey && (reqBody.key = domainFormData.domainKey);
          domainFormData.domainCert && (reqBody.cert = domainFormData.domainCert);
          reqBody.icp = domainFormData.domainICP;
          reqBody.psb = domainFormData.domainPsb;
          reqBody.title = domainFormData.domainTitle;
          try {
            if (domainInfo.domain) {
              // 有域名则进行更新
              const { status } = await smh.userAPI.updateOrgCustomDomain(reqBody);
              if (status === 204) {
                stage.value = 'index';
                tips.success(i18n_t('更新成功'));
              }
            } else {
              const { data: result } = await smh.userAPI.createOrgCustomDomain(reqBody);
              if (result.id) {
                stage.value = 'index';
                tips.success(i18n_t('绑定成功'));
              }
            }
            isSaving.value = false;
          } catch (e: any) {
            errMsg.value = formatError(e);
            isSaving.value = false;
          }
        }
      },
      async unbind() {
        try {
          const { status } = await smh.userAPI.unbindOrgCustomDomain();
          if (status === 204) {
            tips.success(i18n_t('解绑成功'));
            domainInfo.domain = '';
            domainInfo.icp = '';
            domainInfo.psb = '';
            domainInfo.title = '';
            domainInfo.status = -1;
            domainInfo.cipChecked = false;
          }
        } catch (e: any) {
          tips.error(e);
        }
      },
      // 加载域名信息
      async loadDomainInfo() {
        try {
          const { data: list } = await smh.userAPI.getOrgCustomDomain({
            organizationId: orgId.value,
          });
          // 要找出自定义域名
          const [customDomain] = list.filter((item: SMH.CustomDomainData) => item.isCustom);
          if (customDomain) {
            Object.assign(domainInfo, customDomain);
            domainFormData.newDomain = customDomain.domain;
            domainFormData.domainICP = customDomain.icp ?? '';
            domainFormData.domainPsb = customDomain.psb ?? '';
            domainFormData.domainTitle = customDomain.title ?? '';
          }
          // 根据非自定义页面找出entId
          const [systemDomain] = list.filter(
            (item: SMH.CustomDomainData) =>
              /(\.pan)?\.(tencent\.com|cofile\.net)$/i.test(item.domain) && !item.isCustom,
          );

          if (systemDomain) {
            [entId.value] = systemDomain.domain.split('.');
          }
        } catch (e: any) {
          tips.error(e);
        }
        loaded.value = true;
      },
      jumpPsb() {
        dispatchRouter(CONSTANT_URL.psbUrl);
      },
    };
    watch(stage, newVal => {
      if (newVal === 'index') {
        methods.loadDomainInfo();
      }
      if (newVal === 'bind') {
        if (!domainInfo.domain) {
          domainFormData.newDomain = '';
        }
        domainFormData.domainICP = domainInfo.icp ?? '';
        domainFormData.domainPsb = domainInfo.psb ?? '';
        domainFormData.domainTitle = domainInfo.title ?? '';
        domainFormData.domainKey = '';
        domainFormData.domainCert = '';
        errMsg.value = '';
      }
    });
    !store.getters.isPrivate && methods.loadDomainInfo();

    /** 私有化域名改变 */
    const watchInternalUrlChanged = () => {
      internalWebUrl.value = httpUrl.webUrl;
    };
    httpUrl.on(httpUrl.EVENT_MAP.SMHHostChanged, watchInternalUrlChanged);
    onUnmounted(() => {
      httpUrl.off(httpUrl.EVENT_MAP.SMHHostChanged, watchInternalUrlChanged);
    });
    /** 私有化域名改变 */

    return {
      internalWebUrl,
      formReady,
      orgId,
      entId,
      stage,
      loaded,
      domainFormData,
      domainInfo,
      orgNameMaxLength,
      ...methods,
      rules,
      newBindingRules,
      steps,
      errMsg,
      currentDomainStatus,
      isSaving,
      placeholderTxt: {
        cert: i18n_t(
          '仅可粘贴纯文本格式，可修改证书文件后缀为.txt后打开复制文本，证书内容以-----BEGIN CERTIFICATE-----开头，以-----END CERTIFICATE-----结尾',
        ),

        key: i18n_t(
          '仅可粘贴纯文本格式，可修改密钥文件后缀为.txt后打开复制文本，密钥内容以-----BEGIN RSA PRIVATE KEY-----开头，以-----END RSA PRIVATE KEY-----结尾',
        ),
      },
      inWeBrowser,
    };
  },
});
